import { CANDIDATE_STAGES, PIPELINE_STAGES, STAGE_LABELS, TERMINAL_STAGES } from "@lib/types";

const RETURN_TO_POOL_VALUE = "__return_to_pool__";

interface StageDropdownProps {
  currentStage: string;
  onMove: (stage: string) => void;
  /** When provided, renders a "Return to pool…" option that calls this callback instead of onMove. */
  onReturnToPool?: () => void;
}

export function StageDropdown({ currentStage, onMove, onReturnToPool }: StageDropdownProps) {
  const showReturnToPool =
    !!onReturnToPool && (PIPELINE_STAGES as readonly string[]).includes(currentStage);

  return (
    <select
      value=""
      onChange={(e) => {
        const value = e.target.value;
        if (!value) return;
        if (value === RETURN_TO_POOL_VALUE) {
          onReturnToPool?.();
        } else {
          onMove(value);
        }
      }}
      className="rounded-md border border-input-border bg-card px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
    >
      <option value="">Move to…</option>
      {CANDIDATE_STAGES.filter((s) => s !== currentStage).map((s) => (
        <option key={s} value={s}>
          {STAGE_LABELS[s]}
        </option>
      ))}
      {showReturnToPool && (
        <optgroup label="—">
          <option value={RETURN_TO_POOL_VALUE}>Return to pool…</option>
        </optgroup>
      )}
    </select>
  );
}

interface TerminalStageMoveControlProps {
  stage: string;
  onMove: (stage: string) => void;
}

/**
 * "Move to…" control for candidates in a terminal stage (rejected/withdrawn).
 * Every pipeline view renders moved-on candidates read-only, so without this
 * a terminal candidate would be a dead end — there'd be no UI surface left to
 * return them to the pipeline. Renders nothing for non-terminal stages: those
 * candidates are managed from their current stage's own view.
 */
export function TerminalStageMoveControl({ stage, onMove }: TerminalStageMoveControlProps) {
  if (!(TERMINAL_STAGES as readonly string[]).includes(stage)) return null;
  return <StageDropdown currentStage={stage} onMove={onMove} />;
}
